import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SplitShipRowDataPropType } from '../../prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import classnames from 'classnames';
import { Button } from 'ui-components/components/button';
import ItemRowContent from '../columns/item-row-content';
import { isFulfillmentPlanStatusActionable, useSplitShipModalContext } from '../../split-ship-modal-context-provider';
import { SPLIT_SHIP_ACTIONS, useTrackSplitShipAction } from '../../event-tracking/use-track-split-ship-action';
import messages from './messages';
import style from '../items-table-row.module.less';

export const BaseRow = memo(({ disabled, rowData }) => {
  const { dataRowId, name, sku, quantity, fulfillmentPlanId, productId } = rowData;

  const { fulfillmentPlans, moveItemToFulfillmentPlan, setSplitRowId } = useSplitShipModalContext();

  const trackLineItemSplit = useTrackSplitShipAction(SPLIT_SHIP_ACTIONS.LINE_ITEM_SPLIT);
  const trackLineItemMovement = useTrackSplitShipAction(SPLIT_SHIP_ACTIONS.LINE_ITEM_MOVEMENT);

  const selectFulfillmentPlan = useCallback(
    (newFulfillmentPlanId) => {
      const didMoveItem = newFulfillmentPlanId !== fulfillmentPlanId;
      if (didMoveItem) {
        moveItemToFulfillmentPlan(dataRowId, newFulfillmentPlanId);
        trackLineItemMovement();
      }
    },
    [fulfillmentPlanId, moveItemToFulfillmentPlan, dataRowId, trackLineItemMovement],
  );

  const split = useCallback(() => {
    setSplitRowId(rowData.dataRowId);
    trackLineItemSplit();
  }, [rowData.dataRowId, setSplitRowId, trackLineItemSplit]);

  const selectedFulfillmentPlan = fulfillmentPlans.find((x) => x.fulfillmentPlanId === fulfillmentPlanId) || {};

  const isReadOnly = disabled || !isFulfillmentPlanStatusActionable(selectedFulfillmentPlan.orderGridStatus);

  const shouldShowSplitButton = !isReadOnly && quantity >= 2;

  return (
    <tr
      className={classnames(style.row, {
        [style['hoverable-row']]: !disabled,
        [style.disabled]: disabled,
      })}
    >
      <ItemRowContent
        actionsComponent={
          <>
            {shouldShowSplitButton && (
              <Button className={style['split-button']} onClick={split} theme='link'>
                <FormattedMessage {...messages.splitButtonText} />
              </Button>
            )}
          </>
        }
        disabled={disabled}
        fulfillmentPlanId={fulfillmentPlanId}
        isSelectFulfillmentPlanDisabled={isReadOnly}
        name={name}
        onSelectFulfillmentPlan={selectFulfillmentPlan}
        productId={productId}
        quantityComponent={<FormattedNumber value={quantity} />}
        sku={sku}
      />
    </tr>
  );
});

BaseRow.propTypes = {
  disabled: PropTypes.bool.isRequired,
  rowData: SplitShipRowDataPropType.isRequired,
};
